import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { headers } from '@domains/shared/domain/utils/global-constants';
import { webApiUrl } from '@domains/shared/domain/utils/url';
import { Observable } from 'rxjs';
import { UserLogin } from '../../models/user-login.interface';
import { UserDTO } from '../../models/users.dto';
import { TokenPurposeEnum } from '../../models/enums/token-purpose.enum';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private baseUrl = `${webApiUrl}/Auth`;

  constructor(private http: HttpClient) { }

  getUserInfo(): Observable<UserDTO | undefined> {
    const url = [this.baseUrl, 'UserInfo'].join('/');
    return this.http.get<UserDTO>(url);
  }

  loginUser(data: UserLogin): Observable<UserDTO> {
    const url = [this.baseUrl, 'Login'].join('/');
    return this.http.post<UserDTO>(url, data);
  }

  // Odhlášení na serveru: zneplatní token tohoto zařízení (relaci, EL-2343).
  // Token z úložiště maže až facade — request musí odejít ještě s ním.
  logout(): Observable<void> {
    const url = [this.baseUrl, 'Logout'].join('/');
    return this.http.post<void>(url, {}, { headers });
  }

  // Odhlášení všude: zneplatní všechny dosud vydané tokeny uživatele (EL-2290).
  logoutEverywhere(): Observable<void> {
    const url = [this.baseUrl, 'LogoutEverywhere'].join('/');
    return this.http.post<void>(url, {}, { headers });
  }

  // Kolik jiných zařízení je ještě přihlášených (EL-2343).
  otherSessions(): Observable<number> {
    const url = [this.baseUrl, 'OtherSessions'].join('/');
    return this.http.get<number>(url, { headers });
  }

  changeCompany(
    companyId: string,
    url: string = `${webApiUrl}/User/CurrentCompany/${companyId}`
  ): Observable<UserDTO> {
    return this.http.put<UserDTO>(url, null, { headers });
  }

  // Jméno, které jde na certifikát. Přihlašovací e-mail se odsud měnit nedá.
  changeProfile(fullName: string, url: string = `${webApiUrl}/User/Profile`): Observable<UserDTO> {
    return this.http.put<UserDTO>(url, { fullName }, { headers });
  }

  // Změna hesla přihlášeným uživatelem — server vyžaduje i staré heslo.
  changePassword(
    currentPassword: string,
    newPassword: string,
    url: string = `${webApiUrl}/User/Password`
  ): Observable<void> {
    return this.http.put<void>(url, { currentPassword, newPassword }, { headers });
  }

  // Globální vypínač notifikací pro celou osobu.
  changeNotifications(enabled: boolean, url: string = `${webApiUrl}/User/Notifications`): Observable<UserDTO> {
    return this.http.put<UserDTO>(url, { enabled }, { headers });
  }

  // Odebírání notifikací pro jeden kurz.
  changeCourseNotifications(
    studentId: string,
    enabled: boolean,
    url: string = `${webApiUrl}/User/Courses/${studentId}/Notifications`
  ): Observable<void> {
    return this.http.put<void>(url, { enabled }, { headers });
  }

  // Skrytí kurzu z rozcestníku, nebo návrat mezi aktivní.
  changeCourseArchive(
    studentId: string,
    archived: boolean,
    url: string = `${webApiUrl}/User/Courses/${studentId}/Archive`
  ): Observable<void> {
    return this.http.put<void>(url, { archived }, { headers });
  }

  // Odhlášení z odkazu v e-mailu. Běží bez přihlášení, vrací název kurzu.
  /** Jen se zeptá, o jaký kurz jde. Nic nemění — potvrzuje se až tlačítkem na stránce. */
  unsubscribePreview(token: string, url: string = `${webApiUrl}/Unsubscribe/${token}`): Observable<{ courseName: string }> {
    return this.http.get<{ courseName: string }>(url, { headers });
  }

  unsubscribe(token: string, url: string = `${webApiUrl}/Unsubscribe/${token}`): Observable<{ courseName: string }> {
    return this.http.post<{ courseName: string }>(url, {}, { headers });
  }

  // Vlastní změna jazyka přihlášeného uživatele. Backoffice PATCH je role-gated,
  // student na něj nedosáhne.
  changeTheme(
    theme: string,
    url: string = `${webApiUrl}/User/Theme`
  ): Observable<UserDTO> {
    return this.http.put<UserDTO>(url, { theme }, { headers });
  }

  changePageSize(
    pageSize: number,
    url: string = `${webApiUrl}/User/PageSize`
  ): Observable<UserDTO> {
    return this.http.put<UserDTO>(url, { pageSize }, { headers });
  }

  changeLanguage(
    languageIso: string,
    url: string = `${webApiUrl}/User/Language`
  ): Observable<UserDTO> {
    return this.http.put<UserDTO>(url, { languageIso }, { headers });
  }

  resetPassword(data: any): Observable<any> {
    const url = [this.baseUrl, 'Reset-password'].join('/');
    return this.http.post<any>(url, data);
  }

  newPassword(data: any): Observable<any> {
    const url = [this.baseUrl, 'New-password'].join('/');
    return this.http.post<any>(url, data);
  }

  // Jen ověří odkaz z e-mailu, nic nemění. Účel je povinný: aktivační a resetovací
  // token se u druhého účelu neověří (EL-2286).
  checkToken(token: string, purpose: TokenPurposeEnum): Observable<void> {
    const url = [this.baseUrl, 'CheckToken'].join('/');
    return this.http.post<void>(url, { token, purpose });
  }

  activateAccount(data: any): Observable<any> {
    const url = [this.baseUrl, 'Activate-account'].join('/');
    return this.http.post<any>(url, data);
  }
}
